import React, { memo, useState, useCallback, useMemo } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { AppRow } from '../../layout/appRow';
import { Client } from '../../models/client';
import { AppToolbox } from '../app/appToolbox';
import { AppButtonContained } from '../app/button/appButtonContained';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FormProvider, useForm } from 'react-hook-form';
import { AppControlledCheckbox } from '../app/input/appControlledCheckbox';
import { AppControlledTextField } from '../app/input/appControlledTextField';
import { useDebounceFn } from '../../hooks/useDebounce';
import { GetProjectsParams } from '../../services/api/projectService';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';
import {
  useGetClientsQuery,
  useGetEmployeesQuery,
  useGetSalesPersonsQuery,
} from '../../store/employees/employeesService';
import { SearchOptions } from '../../models/reports';
import { useUserRoles } from '../../hooks/useUserRoles';
import { ROLES_ID } from '../../models/employee';
import { getOptions } from '../../services/employeeService';
import { AutocompleteOption } from '../../models/autocompleteOption';
import { DEFAULT_PAGE_SIZE, NO_RESULTS, PLEASE_TYPE } from '../../constants';
import { useGetTowersQuery } from '../../store/towers/towersService';
import { toolboxStyles } from '../../styles/constants';

enum SearchField {
  CLIENT = 'clientId',
  BUDGER_OWNER = 'budgetOwnerId',
  PROJECT_MANAGER = 'projectManagerId',
  TOWER = 'towerId',
  FILTER = 'filter',
  SALES_PERSON = 'salesPersonId',
  ONGOING_PROJECTS_ONLY = 'ongoingProjectsOnly',
}

interface ProjectsToolboxFormValues {
  filter: string;
  clientId: AutocompleteOption | null;
  budgetOwnerId: AutocompleteOption | null;
  projectManagerId: AutocompleteOption | null;
  towerId: AutocompleteOption | null;
  salesPersonId: AutocompleteOption | null;
  ongoingProjectsOnly: boolean;
}

interface Props {
  onOpenAddProject(): void;
  onSearch(params: GetProjectsParams): void;
  client: Client | null;
  onSwitch(): void;
  isFromClient: boolean | undefined;
  loadingClient: boolean;
}

export function ProjectsToolboxBase({
  onSearch,
  onOpenAddProject,
  client,
  onSwitch,
  isFromClient,
  loadingClient,
}: Props): JSX.Element {
  const debounce = useDebounceFn(250);
  const methods = useForm<ProjectsToolboxFormValues>();

  const { isAdmin, isProjectManager, isBudgetOwner } = useUserRoles();

  const { watch } = methods;

  const [watchedClient, watchedBudgetOwner, watchedProjectManager, watchedTower, watchedSalesPerson] = watch([
    SearchField.CLIENT,
    SearchField.BUDGER_OWNER,
    SearchField.PROJECT_MANAGER,
    SearchField.TOWER,
    SearchField.SALES_PERSON,
  ]);

  const [searchOptions, setSearchOptions] = useState<
    SearchOptions<SearchField>
  >({
    value: '',
  });

  const { data: clientsQueryData, isFetching: isFetchingClients } =
    useGetClientsQuery({
      take: DEFAULT_PAGE_SIZE,
      ...{
        filter:
          searchOptions.field === SearchField.CLIENT ? searchOptions.value : '',
      },
  });

  const { data: towerQueryData, isFetching: isFetchingTowers } =
    useGetTowersQuery({
      take: DEFAULT_PAGE_SIZE,
      ...{
        filter:
          searchOptions.field === SearchField.TOWER ? searchOptions.value : '',
      },
  });

  const { data: budgetOwnersQueryData, isFetching: isFetchingBudgetOwners } =
    useGetEmployeesQuery({
      take: DEFAULT_PAGE_SIZE,
      includeInactive: true,
      roleId: ROLES_ID.SALES_MANAGER,
      ...{
        filter:
          searchOptions.field === SearchField.BUDGER_OWNER
            ? searchOptions.value
            : '',
      },
  });

  const {
    data: projectManagersQueryData,
    isFetching: isFetchingProjectManagers,
  } = useGetEmployeesQuery({
    take: DEFAULT_PAGE_SIZE,
    includeInactive: true,
    roleId: ROLES_ID.PROJECT_MANAGER,
    ...{
      filter:
        searchOptions.field === SearchField.PROJECT_MANAGER
          ? searchOptions.value
          : '',
    },
  });

  const { data: salesPersonsQueryData, isFetching: isFetchingSalesPersons } =
    useGetSalesPersonsQuery({
      take: DEFAULT_PAGE_SIZE,
      includeInactive: true,
      ...{
        filter:
          searchOptions.field === SearchField.SALES_PERSON
            ? searchOptions.value
            : '',
      },
    });

  const projectManagersOptions = useMemo(() => {
    return projectManagersQueryData
      ? getOptions(projectManagersQueryData?.items)
      : [];
  }, [projectManagersQueryData]);

  const budgetOwnersOptions = useMemo(() => {
    return budgetOwnersQueryData
      ? getOptions(budgetOwnersQueryData?.items)
      : [];
  }, [budgetOwnersQueryData]);

  const clientsOptions = useMemo(() => {
    return clientsQueryData ? getOptions(clientsQueryData?.items) : [];
  }, [clientsQueryData]);

  const towerOptions = useMemo(() => {
    return towerQueryData ? getOptions(towerQueryData?.items) : [];
  }, [towerQueryData]);

  const salesPersonsOptions = useMemo(() => {
    return salesPersonsQueryData
      ? getOptions(salesPersonsQueryData?.items)
      : [];
  }, [salesPersonsQueryData]);

  const getParams = useCallback(
    (data?, field?: SearchField) => {
      const formValues = methods.getValues();

      const params = {
        ...formValues,
        clientId: (watchedClient as AutocompleteOption)?.id || '',
        budgetOwnerId: (watchedBudgetOwner as AutocompleteOption)?.id || '',
        projectManagerId:
          (watchedProjectManager as AutocompleteOption)?.id || '',
        towerId:
          (watchedTower as AutocompleteOption)?.id || '',
        salesPersonId:
          (watchedSalesPerson as AutocompleteOption)?.id || '',
        ongoingProjectsOnly: !formValues.ongoingProjectsOnly,
      };

      switch (field) {
        case SearchField.CLIENT:
          return { ...params, clientId: data?.id };

        case SearchField.BUDGER_OWNER:
          return { ...params, budgetOwnerId: data?.id };

        case SearchField.PROJECT_MANAGER:
          return { ...params, projectManagerId: data?.id };

        case SearchField.TOWER:
          return { ...params, towerId: data?.id };

        case SearchField.SALES_PERSON:
          return { ...params, salesPersonId: data?.id };

        default:
          return params;
      }
    },
    [methods, watchedBudgetOwner, watchedClient, watchedProjectManager, watchedTower, watchedSalesPerson]
  );

  const onResetSearch = useCallback(() => {
    setSearchOptions({ value: '' });
  }, []);

  const searchProjectsByFilter = useCallback(
    (e) => {
      debounce(() => {
        const filterData = e.target.value;

        onSearch({
          ...getParams(),
          filter: filterData,
        });
      });
    },
    [getParams, debounce, onSearch]
  );

  const searchProjectsByProjectManager = useCallback(
    (data) => {
      onSearch(getParams(data, SearchField.PROJECT_MANAGER));
      onResetSearch();
    },
    [getParams, onResetSearch, onSearch]
  );

  const searchProjectsByBudgetOwner = useCallback(
    (data) => {
      onSearch(getParams(data, SearchField.BUDGER_OWNER));
      onResetSearch();
    },
    [getParams, onResetSearch, onSearch]
  );

  const searchProjectsByClient = useCallback(
    (data) => {
      onSearch(getParams(data, SearchField.CLIENT));
      onResetSearch();
    },
    [getParams, onResetSearch, onSearch]
  );

  const searchProjectsByTower = useCallback(
    (data) => {
      onSearch(getParams(data, SearchField.TOWER));
      onResetSearch();
    },
    [getParams, onResetSearch, onSearch]
  );

  const searchProjectsByOngoingProjectsOnly = useCallback(() => {
    onSearch(getParams());
  }, [getParams, onSearch]);

  const searchProjectManagerOptions = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.PROJECT_MANAGER });
  }, []);

  const searchBudgetOwnerOptions = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.BUDGER_OWNER });
  }, []);

  const searchClientOptions = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.CLIENT });
  }, []);

  const searchTowerOptions = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.TOWER });
  }, []);

  const searchSalesPersonOptions = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.SALES_PERSON });
  }, []);

  const searchProjectsBySalesPerson = useCallback(
    (data) => {
      onSearch(getParams(data, SearchField.SALES_PERSON));
      onResetSearch();
    },
    [getParams, onResetSearch, onSearch]
  );

  return (
    <AppToolbox
      pageTitle={'Projects'}
      secondChildren={
        <AppButtonContained
          label={'Project'}
          handleClick={onOpenAddProject}
          isIconButton
        />
      }
      thirdChildren={
        <Box sx={{ mb: 2 }}>
          <FormProvider {...methods}>
            <Box sx={toolboxStyles.inputsBox}>
              <AppControlledTextField
                name={SearchField.FILTER}
                label={'Project'}
                sx={toolboxStyles.input}
                onChangeFn={searchProjectsByFilter}
              />

              {(isAdmin || isBudgetOwner) && (
                <AppControlledAutocomplete
                  name={SearchField.PROJECT_MANAGER}
                  label={'Project manager'}
                  searchOptions={searchProjectManagerOptions}
                  options={projectManagersOptions}
                  sx={toolboxStyles.input}
                  onSelect={searchProjectsByProjectManager}
                  placeholder={PLEASE_TYPE}
                  isLoading={isFetchingProjectManagers}
                  noOptionsText={NO_RESULTS}
                />
              )}
              {(isAdmin || isProjectManager) && (
                <AppControlledAutocomplete
                  name={SearchField.BUDGER_OWNER}
                  label={'Backup Project Manager'}
                  searchOptions={searchBudgetOwnerOptions}
                  options={budgetOwnersOptions}
                  sx={toolboxStyles.input}
                  onSelect={searchProjectsByBudgetOwner}
                  placeholder={PLEASE_TYPE}
                  isLoading={isFetchingBudgetOwners}
                  noOptionsText={NO_RESULTS}
                />
              )}
              {!client && (
                <>
                  <AppControlledAutocomplete
                    name={SearchField.CLIENT}
                    label={'Client'}
                    searchOptions={searchClientOptions}
                    options={clientsOptions}
                    sx={toolboxStyles.input}
                    onSelect={searchProjectsByClient}
                    placeholder={PLEASE_TYPE}
                    isLoading={isFetchingClients}
                    noOptionsText={NO_RESULTS}
                  />
                  <AppControlledAutocomplete
                    name={SearchField.SALES_PERSON}
                    label={'Sales'}
                    searchOptions={searchSalesPersonOptions}
                    options={salesPersonsOptions}
                    sx={toolboxStyles.input}
                    onSelect={searchProjectsBySalesPerson}
                    placeholder={PLEASE_TYPE}
                    isLoading={isFetchingSalesPersons}
                    noOptionsText={NO_RESULTS}
                  />
                </>
              )}
              <AppControlledAutocomplete
                name={SearchField.TOWER}
                label={'Tower'}
                searchOptions={searchTowerOptions}
                options={towerOptions}
                sx={toolboxStyles.input}
                onSelect={searchProjectsByTower}
                placeholder={PLEASE_TYPE}
                isLoading={isFetchingTowers}
                noOptionsText={NO_RESULTS}
              />
              <Box
                sx={{ ...toolboxStyles.input, display: 'flex', alignItems: 'center' }}
              >
                <AppControlledCheckbox
                  name={SearchField.ONGOING_PROJECTS_ONLY}
                  label={'Show also inactive'}
                  labelSx={{ mr: 1 }}
                  defaultValue={false}
                  onChangeFn={searchProjectsByOngoingProjectsOnly}
                />
              </Box>
            </Box>
          </FormProvider>
          {isFromClient && (
            <Box sx={{ flexDirection: 'column' }}>
              <AppRow sx={toolboxStyles.appRow}>
                <Button onClick={onSwitch}>
                  <ArrowBackIcon fontSize={'large'} />
                </Button>
                {loadingClient ? (
                  <Skeleton
                    animation="wave"
                    sx={{ height: '40px', width: '100px', ml: 3 }}
                  />
                ) : (
                  <Typography ml={3} variant="h4">
                    {`${client?.name}'s Projects`}
                  </Typography>
                )}
              </AppRow>
            </Box>
          )}
        </Box>
      }
    />
  );
}

export const ProjectsToolbox = memo(ProjectsToolboxBase);
